/**
 * @file App menu (snippets/app-menu.liquid). Registers <bk-app-menu>, a BkDialog (@theme/dialog:
 * modal <dialog>, focus trap, scroll lock, Escape, backdrop click, return focus) around the
 * full-screen category screen.
 *
 * - Search: the row at the top opens the store's one search dialog. This menu closes first, so two
 *   modals never stack and focus lands in the search field.
 * - Recent categories: the last 4 the visitor opened, in localStorage["bk:app-menu-recent"] as
 *   [{ u: url, t: label }]. Both the url and the label are server-rendered values (the current
 *   category is handed over in data-current-url / data-current-label, a tile carries its own), so
 *   nothing is ever invented here. Storage can be blocked: every read and write is guarded and the
 *   section simply stays hidden. The Clear button empties it and announces that it did.
 * - Swipe down closes, like a sheet in an app: the panel follows the finger once the gesture passes
 *   the tap slop and only when the body is already scrolled to the top. A drag never navigates: the
 *   click that follows it is swallowed.
 * - Takeover: while data-takeover="1" and the viewport is narrow, a click on the drawer's openers
 *   ([data-dialog-open="MenuDrawer"], [data-menu-drawer-open]) opens this menu instead. The listener
 *   runs in the capture phase and stops the event, so neither BkDialog nor menu-drawer.js sees it.
 *   That is how the mobile tab bar's "Categories" tab opens this menu without knowing it exists, and
 *   why the drawer keeps working untouched for stores that choose it.
 *
 * All visible strings arrive translated in the markup (aria-labels, data-label-cleared).
 */
import { BkDialog } from '@theme/dialog';

const RECENT_KEY = 'bk:app-menu-recent';
const RECENT_MAX = 4;

/** Below this width the drawer's openers belong to the app menu. */
const TAKEOVER_QUERY = '(max-width: 989.98px)';
const OPENER_SELECTOR = '[data-dialog-open="MenuDrawer"], [data-menu-drawer-open]';

/** Movement still counted as a tap, in px. */
const TAP_SLOP = 10;
/** Downward travel that closes the sheet, in px. */
const CLOSE_MIN = 110;
/** A click arriving this soon after a drag is the drag's own click. */
const DRAG_CLICK_MS = 300;

/** @returns {{ u: string, t: string }[]} The visitor's recent categories, newest first. */
function readRecent() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry) => entry && typeof entry.u === 'string' && typeof entry.t === 'string' && entry.u && entry.t)
      .slice(0, RECENT_MAX);
  } catch {
    // Storage blocked (private mode, policy): there is simply nothing recent to show.
    return [];
  }
}

/** @param {{ u: string, t: string }[]} list */
function writeRecent(list) {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    // Storage blocked: the list lives for this page only.
  }
}

/**
 * Remember a category the visitor opened, newest first, without duplicates.
 * @param {string} url
 * @param {string} label - Server-rendered category name
 */
function remember(url, label) {
  if (!url || !label) return;
  const list = readRecent().filter((entry) => entry.u !== url);
  list.unshift({ u: url, t: label });
  writeRecent(list);
}

/**
 * @typedef {object} SheetPress
 * @property {number} id - Pointer id
 * @property {number} y - Start position
 * @property {number} dy - Travel so far
 * @property {boolean} dragging
 */

class BkAppMenu extends BkDialog {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLDialogElement | null} */
  #panel = null;

  /** @type {HTMLElement | null} */
  #body = null;

  /** @type {HTMLElement | null} */
  #recent = null;

  /** @type {HTMLElement | null} */
  #recentList = null;

  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {HTMLElement | null} */
  #search = null;

  /** @type {SheetPress | null} */
  #press = null;

  #draggedAt = 0;

  #mobile = matchMedia(TAKEOVER_QUERY);

  connectedCallback() {
    super.connectedCallback();

    const panel = this.querySelector('dialog');
    this.#panel = panel instanceof HTMLDialogElement ? panel : null;
    this.#body = this.querySelector('[data-app-menu-body]');
    this.#recent = this.querySelector('[data-app-menu-recent]');
    this.#recentList = this.querySelector('[data-app-menu-recent-list]');
    this.#status = this.querySelector('[data-app-menu-status]');
    this.#search = this.querySelector('[data-app-menu-search]');

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    document.addEventListener('click', (event) => this.#onTakeover(event), { capture: true, signal });

    if (this.#panel) {
      const panelElement = this.#panel;
      panelElement.addEventListener('pointerdown', (event) => this.#onPointerDown(event), { signal });
      panelElement.addEventListener('pointermove', (event) => this.#onPointerMove(event), { signal });
      panelElement.addEventListener('pointerup', (event) => this.#onPointerUp(event), { signal });
      panelElement.addEventListener('pointercancel', () => this.#resetPress(), { signal });
      // A swipe must not follow the tile it started on.
      panelElement.addEventListener('click', (event) => this.#swallowDragClick(event), { capture: true, signal });
    }

    // The category being read right now, handed over by the server.
    remember(this.dataset.currentUrl ?? '', this.dataset.currentLabel ?? '');
    this.#renderRecent();
  }

  disconnectedCallback() {
    this.#resetPress();
    this.#controller?.abort();
    this.#controller = null;
    super.disconnectedCallback();
  }

  /** @param {HTMLElement | null} [trigger] */
  open(trigger) {
    // Read the list again on every opening: another tab may have added to it.
    this.#renderRecent();
    super.open(trigger);
  }

  /** Draw the recent categories, or hide the whole section when there are none. */
  #renderRecent() {
    const section = this.#recent;
    const list = this.#recentList;
    if (!section || !list) return;

    const entries = readRecent();
    list.replaceChildren();
    for (const entry of entries) {
      const item = document.createElement('li');
      item.className = 'app-menu__recent-item';
      const link = document.createElement('a');
      link.className = 'chip chip--choice app-menu__recent-link';
      link.href = entry.u;
      link.textContent = entry.t;
      item.append(link);
      list.append(item);
    }
    section.hidden = entries.length === 0;
  }

  #clearRecent() {
    writeRecent([]);
    this.#renderRecent();
    const label = this.dataset.labelCleared;
    if (this.#status && label) this.#status.textContent = label;
    this.#search?.focus();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-app-menu-recent-clear]')) {
      event.preventDefault();
      this.#clearRecent();
      return;
    }

    if (target.closest('[data-app-menu-search]')) {
      // Stop here so BkDialog's document listener does not open the search on top of this menu.
      event.preventDefault();
      event.stopPropagation();
      this.#openSearch();
      return;
    }

    const tile = target.closest('[data-app-menu-tile]');
    if (tile instanceof HTMLElement) remember(tile.dataset.appMenuUrl ?? '', tile.dataset.appMenuLabel ?? '');
  }

  /** Close this menu, then hand the visitor to the store's search dialog. */
  #openSearch() {
    const dialog = /** @type {any} */ (document.getElementById('SearchDialog'));
    this.close();
    if (!dialog || typeof dialog.open !== 'function') return;
    requestAnimationFrame(() => dialog.open(null));
  }

  /**
   * The drawer's openers belong to this menu while it is the store's mobile menu.
   * @param {MouseEvent} event
   */
  #onTakeover(event) {
    if (this.dataset.takeover !== '1' || !this.#mobile.matches) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target instanceof Element ? event.target : null;
    const opener = target?.closest(OPENER_SELECTOR);
    if (!(opener instanceof HTMLElement) || this.contains(opener)) return;
    if (document.querySelector('bk-app-menu') !== this) return;

    event.preventDefault();
    event.stopPropagation();
    this.open(opener);
  }

  /** @param {PointerEvent} event */
  #onPointerDown(event) {
    if (!event.isPrimary || event.pointerType === 'mouse') return;
    // Only from the top of the list: below that, a downward drag is a scroll.
    if (this.#body && this.#body.scrollTop > 0) return;
    this.#press = { id: event.pointerId, y: event.clientY, dy: 0, dragging: false };
  }

  /** @param {PointerEvent} event */
  #onPointerMove(event) {
    const press = this.#press;
    if (!press || event.pointerId !== press.id) return;
    press.dy = event.clientY - press.y;

    if (!press.dragging) {
      if (press.dy <= TAP_SLOP) return;
      press.dragging = true;
      try {
        // Throws when the pointer is already gone (or was never captured by this element).
        this.#panel?.setPointerCapture?.(event.pointerId);
      } catch {
        // The gesture still works without capture.
      }
      this.#panel?.toggleAttribute('data-dragging', true);
    }

    const down = Math.max(0, press.dy);
    if (this.#panel) this.#panel.style.translate = `0 ${down}px`;
  }

  /** @param {PointerEvent} event */
  #onPointerUp(event) {
    const press = this.#press;
    if (!press || event.pointerId !== press.id) return;
    const { dy, dragging } = press;
    this.#resetPress();
    if (!dragging) return;
    this.#draggedAt = Date.now();
    if (dy > CLOSE_MIN) this.close();
  }

  #resetPress() {
    this.#press = null;
    const panel = this.#panel;
    if (!panel) return;
    panel.style.translate = '';
    panel.removeAttribute('data-dragging');
  }

  /** @param {MouseEvent} event */
  #swallowDragClick(event) {
    if (Date.now() - this.#draggedAt > DRAG_CLICK_MS) return;
    event.preventDefault();
    event.stopPropagation();
  }
}

if (!customElements.get('bk-app-menu')) customElements.define('bk-app-menu', BkAppMenu);
